import { bridgeFailure, invalidInput, openNotebookBridge } from './execute-live-notebook'

/** The cell-op contract's cap (`services/console/ai/src/tools/scripts/shared/cellOps.ts`); the bridge checks every op. */
const MAX_OPS = 30

export const EDITED = 'Edited in the open notebook; it saves as usual.'

interface ApplyOpsResult {
  changed?: unknown
  cell_count?: unknown
}

/**
 * `edit_notebook`: applies cell ops to the notebook open in a live session, through the JupyterLab
 * bridge (one undoable step; never saves). The bridge validates each op and refuses the whole batch
 * on any bad one (`invalid_args` / `op_failed` with `op_index`), and refuses while a run is going (`busy`).
 */
export async function executeEditNotebook(input: unknown): Promise<unknown> {
  const open = openNotebookBridge(input)
  if ('success' in open) return open
  const { notebookId, bridge } = open

  const ops = (input as { ops?: unknown }).ops
  if (!Array.isArray(ops) || ops.length === 0)
    return invalidInput('`ops` must be a non-empty list of cell edits.', notebookId)
  if (ops.length > MAX_OPS) return invalidInput(`At most ${MAX_OPS} ops in one edit_notebook call.`, notebookId)
  if (ops.some(op => typeof op !== 'object' || op === null || Array.isArray(op))) {
    return invalidInput('Each op must be an object with an `op`.', notebookId)
  }

  try {
    const result = await bridge.request<ApplyOpsResult>('apply_ops', { ops })

    return {
      success: true,
      notebook_id: notebookId,
      changed: Array.isArray(result?.changed) ? result.changed : [],
      cell_count: typeof result?.cell_count === 'number' ? result.cell_count : null,
      message: EDITED,
    }
  } catch (err) {
    return bridgeFailure(notebookId, err)
  }
}
